import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { scaledCoordinate } from '../drawing.helper'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'

export type GannFanDrawingState = {
    p1?: Point,
    p2?: Point
}

export type GannFanDrawingSettings = {
    lineWidth: number,
    labelColor: ColorValue,
}

class GannFanDrawingTool extends DrawingBase<any, GannFanDrawingSettings, GannFanDrawingState> {

    area: number = 0
    scaledCoordinates = {
        p1x: 0,
        p2x: 0,
        p1y: 0,
        p2y: 0
    }
    sectionLevels = [2, 3, 4, 8]
    sections: Array<number[]> = []
    slopes: number[] = []
    sectionColors: string[] = ['#f23645', '#e91e63', '#9c27b0', '#2962ff', '#00bcd4', '#089981', '#4caf50', '#089981', '#ff9800']

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context
        this.calculateAttributes()
        let { p1x, p2x, p1y, p2y } = this.scaledCoordinates
        if (!p1x || !p2x || !p1y || !p2y) return

        let previousLevel: number[] = []
        this.sections.map((s, i) => {
            const lineColor = this.sectionColors[i]

            ctx.globalAlpha = 0.7
            ctx.strokeStyle = lineColor
            ctx.lineWidth = this._settings.lineWidth * window.devicePixelRatio
            ctx.beginPath()
            ctx.moveTo(p1x, p1y)
            ctx.lineTo(s[0], s[1])
            ctx.stroke()
            if (previousLevel.length) {
                const fillColor = this.sectionColors[i < 5 ? i - 1 : i]
                ctx.moveTo(p1x, p1y)
                ctx.lineTo(previousLevel[0], previousLevel[1])
                ctx.lineTo(s[0], s[1])
                ctx.closePath()
                ctx.globalAlpha = 0.15
                ctx.fillStyle = fillColor
                ctx.fill()
            }

            previousLevel = s
        })

        this.renderSectionText(ctx)
    };

    renderSectionText(ctx: CanvasRenderingContext2D) {
        let { p1x, p2x, p1y, p2y } = this.scaledCoordinates
        if (!p1x || !p2x || !p1y || !p2y) return
        ctx.font = 12 * window.devicePixelRatio + 'px sans-serif'
        ctx.textBaseline = 'top'
        ctx.globalAlpha = 1
        let i = 0;
        [1, ...this.sectionLevels].reverse().forEach((s) => {
            ctx.fillStyle = this.sectionColors[i]
            const section = this.sections[i]
            const text = `${s}/1`
            const startX = p2x + 5
            const startY = section[2]
            ctx.fillText(text, startX, startY)
            i += 1
        })
        this.sectionLevels.forEach((s) => {
            ctx.fillStyle = this.sectionColors[i]
            const section = this.sections[i]
            const text = `1/${s}`
            const startX = section[2]
            const startY = p2y
            ctx.fillText(text, startX, startY)
            i += 1
        })

    }

    calculateSections() {

        const { p1x, p1y, p2x, p2y } = this.scaledCoordinates
        if (!p1x || !p1y || !p2x || !p2y) return

        this.sections = []
        this.slopes = []
        const rangeY = p1y - p2y
        const rangeX = p2x - p1x

        const endY = p2y > p1y ? this.canvasHeight : 0;

        [...this.sectionLevels].reverse().forEach((s) => {
            const x = p2x, y = p1y - rangeY / s
            const slope = (y - p1y) / (x - p1x)
            const intercept = y - slope * x
            let endX = (endY - intercept) / slope
            this.slopes.push(slope)
            this.sections.push([endX, endY, y])
        })
        const slope = -rangeY / rangeX
        const intercept = p1y - slope * p1x
        let endX = (endY - intercept) / slope
        this.slopes.push(slope)
        this.sections.push([endX, endY, p2y])

        this.sectionLevels.forEach((s) => {
            const x = p1x + rangeX / s, y = p2y
            const slope = (y - p1y) / (x - p1x)
            const intercept = y - slope * x
            let endX = (endY - intercept) / slope
            this.slopes.push(slope)
            this.sections.push([endX, endY, x])
        })
    }

    calculateAttributes() {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return

        let p1x = this.timeToCoordinate(p1.x)
        let p2x = this.timeToCoordinate(p2.x)
        let p1y = this.priceToCoordinate(p1.y)
        let p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            this.scaledCoordinates.p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
        }
        this.calculateSections()
    }


    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {

        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.p1 = point
        } else if (this.lastAddedPoint.id == 'p1') {
            point.id = 'p2'
            this._state.p2 = point
            !preview && this.onDrawingEnd()
        }
        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2 } = this._state

        p1 && handles.push(p1)
        p2 && handles.push(p2)

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { p1x, p1y, p2x, p2y } = this.scaledCoordinates

        // rule out if not in same quadrant;
        if ((p2x > p1x && scaledX < p1x) || (p2x < p1x && scaledX > p1x) || (p2y > p1y && scaledY < p1y) || (p2y < p1y && scaledY > p1y)) return null

        const slope = (scaledY - p1y) / (scaledX - p1x)
        const minSlope = Math.min(this.slopes[0], this.slopes[this.slopes.length - 1])
        const maxSlope = Math.max(this.slopes[0], this.slopes[this.slopes.length - 1])

        if (slope >= minSlope && slope <= maxSlope) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }

        return null
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        this._state[id as keyof typeof this._state]!.x = time
        this._state[id as keyof typeof this._state]!.y = price
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {

        const { p1, p2 } = this._state
        if (!p1 || !p2) return []

        const sorted = [p1, p2].sort((a, b) => a.y - b.y)
        return [{
            id: 'price',
            p1: sorted[0].y,
            p2: sorted[1].y,
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]

    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []

        const sorted = [p1, p2].sort((a, b) => (a.x as number) - (b.x as number))
        return [{
            id: 'timer',
            p1: sorted[0].x,
            p2: sorted[1].x,
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }


    getAvailableSettings = (): DrawingSettingGroup<GannFanDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#6397d4',
                        opacity: 0.7
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 1,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): GannFanDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof GannFanDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = ['labelColor', 'lineWidth']
            }
        })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'gannFan'
    }
}

export default GannFanDrawingTool